<template>
  <div class="quiz">
    <div class="quiz-title">
      <h2 class="h2">{{ $t('quiz.title') }}</h2>
      <button
        class="quiz-title-button"
        @click="toggleModal('rulesDisplayed')">
        <sprite
          :type="'info'"
          :width="16"
          :height="16"
        />
        <span class="u-visually-hidden">{{ $t('quiz.rules_show') }}</span>
      </button>
    </div>
    <div class="quiz-head">
      <div class="quiz-head-info">
        <div class="quiz-head-picture">
            <img
              :src="thumbnail.src"
              :srcset="thumbnail.src2x ? `${thumbnail.src2x} 2x` : null"
              :alt="thumbnail.alt"
            >
            <span class="year-l">{{ year }}</span>
        </div>
        <div class="quiz-head-content">
          <h3 class="h3" v-html="title"></h3>
        </div>
      </div>
      <div :class="completed ? 'quiz-head-counter completed' : 'quiz-head-counter'">
          <svg
            class="counter-svg"
          >
            <g class="counter-circle">
              <circle
                class="counter-path"
                cx="43"
                cy="43"
                r="41"
              />
              <circle
                v-if="timeLeft !== 0"
                :stroke-dasharray="circleDasharray"
                class="counter-path-remaining"
                cx="43"
                cy="43"
                r="42"
              />
            </g>
          </svg>
          <div class="counter-label">
            <span class="score">{{ score }}</span>
            <span class="unit">{{ score === 0 ? $t('labels.point') : $t('labels.points') }}</span>
          </div>
      </div>
    </div>
    <div class="quiz-content">
      <transition name="fade">
        <div class="quiz-step-1" v-if="!timer && !completed && !quizLoading">
          <h2 class="quiz-step-1-title h4">{{ $t('quiz.start_title') }}</h2>
          <p class="quiz-step-1-text" v-html="$t('quiz.start_text')"></p>
          <btn :class="'tertiary'"
          @click="startQuiz">
            {{ $t('quiz.start_cta') }}
            <sprite
              :type="'arrow-right'"
              :width="19"
              :height="12"
            />
          </btn>
        </div>
      </transition>
      <transition name="fade">
        <loader v-if="quizLoading"></loader>
        <div class="quiz-step-2" v-if="(timer || completed) && !quizLoading">
          <p class="quiz-question">{{ quiz.question }}</p>
          <ul class="quiz-answers">
              <li  v-for="(answer, index) in quiz.answers" :key="`answer-${index}`" :class="['quiz-answer', { 'disabled' : clicked}]">
                  <button
                    v-if="answer"
                    class="quiz-button"
                    @click="endQuiz($event)"
                    :data-id="answer.id">
                    <span>{{ answer.text }}</span>
                  </button>
              </li>
          </ul>
        </div>
      </transition>
    </div>
    <modal
      :isDisplayed="answerDisplayed"
      :id="'modal-quiz'"
      :title="modalTitle"
      @closeModal="toggleModal('answerDisplayed')">
      <div class="quiz-modal">
        <div :class="success ? 'quiz-modal-counter' : 'quiz-modal-counter wrong'">
          <svg
            class="counter-svg"
          >
            <g class="counter-circle">
              <circle
                class="counter-path"
                cx="43"
                cy="43"
                r="41"
              />
              <circle
                v-if="timeLeft !== 0"
                :stroke-dasharray="circleDasharray"
                class="counter-path-remaining"
                cx="43"
                cy="43"
                r="42"
              />
            </g>
          </svg>
          <div class="counter-label">
            <span class="score">{{ score }}</span>
            <span class="unit">{{ score === 0 ? 'pt' : 'pts' }}</span>
          </div>
        </div>
        <h4 class="quiz-modal-title h3"><span>{{ $t('quiz.correct_answer') }}</span> {{ correctAnswer.text }}</h4>
        <div class="quiz-modal-text"><p v-html="correctAnswer.correct_text"></p></div>
        <btn @click="toggleModal('answerDisplayed')">{{ $t('back.modal') }}</btn>
      </div>
    </modal>
    <modal
      :isDisplayed="rulesDisplayed"
      :id="'modal-rules'"
      :title="$t('quiz.rules_title')"
      @closeModal="toggleModal('rulesDisplayed')">
      <div class="rules">
        <loader v-if="rulesLoading" :dark="true" />
        <div class="rules-text typeset" v-else v-html="rules"></div>
        <btn @click="toggleModal('rulesDisplayed')">{{ $t('quiz.rules_cta') }}</btn>
      </div>
    </modal>
  </div>
</template>

<script>
import Btn from '@/components/components/Btn.vue';
import Loader from '@/components/components/Loader.vue';
import Sprite from '@/components/components/Sprite.vue';
import Modal from '@/components/partials/Modal.vue';

import toggleModal from '@/mixins/toggleModal';

export default {
  components: {
    Btn,
    Loader,
    Sprite,
    Modal
  },
  mixins: [toggleModal],
  async beforeMount() {
    // Reset the quiz before loading the component
    await this.$store.dispatch('quiz/reset');

    this.score = this.initialScore;
    this.$store.dispatch('user/updateTempScore', this.userTotalScore);

    if (this.completed) {
      const { locale } = this.$route.params;
      this.score = this.completed.score;
      await this.$store.dispatch('quiz/get', { id: this.id, loc: locale });
      this.styleAnswers();
      this.disableBtns();
    }
  },
  beforeDestroy() {
    clearInterval(this.timer);
    const exhibitionId = this.$route.params.id;

    if (this.start) {
      this.$store.dispatch('user/get', exhibitionId);
    }
    this.$store.dispatch('user/updateTempScore', null);
  },
  data() {
    return {
      clicked: false, 
      quizLoading: false,
      start: null,
      currentTime: null,
      initialScore: 200,
      score: null,
      timer: null,
      timeLimit: 200,
      timePassed: 0,
      selected: null,
      success: false,
      answerDisplayed: false,
      rulesDisplayed: false,
      modalTitle: this.$i18n.t('quiz.wrong'),
      correctAnswer: {
        id: undefined,
        text: undefined
      },
    };
  },
  props: {
    thumbnail: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    year: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: true
    },
    completed: {
      type: Object,
      default: null
    }
  },
  computed: {
    quiz() {
      return this.$store.state.quiz.item;
    },
    rules() {
      return this.$store.state.terms.item.rules;
    },
    rulesLoading() {
      return this.$store.state.terms.requests !== 0;
    },
    timeLeft() {
      return this.timeLimit - this.timePassed;
    },
    timeFraction() {
      return this.timeLeft / this.timeLimit;
    },
    circleDasharray() {
      return `${(this.timeFraction * 264).toFixed(0)} 264`;
    },
    userTotalScore() {
      return this.$store.state.user.item.score;
    }
  },
  methods: {
    async startQuiz() {
      const { locale } = this.$route.params;
      console.log(locale)
      this.quizLoading = true;
      await this.$store.dispatch('quiz/get', { id: this.id, loc: locale });
      this.quizLoading = false;
      this.start = new Date();
      this.timer = setInterval(this.countdown.bind(this), 100);
    },
    countdown() {
      this.currentTime = new Date();
      const count = Math.abs(this.currentTime - this.start);
      this.timePassed = Math.floor(count / 100);
      this.score = this.initialScore - (Math.floor(count / 1000) * 10);
      if (this.timePassed < this.timeLimit) {
        return this.timePassed;
      }
      clearInterval(this.timer);
      this.modalTitle = this.$i18n.t('quiz.timeout');
      this.endQuiz(null);
      this.score = 0;
      return false;
    },
    async endQuiz(e) {
      this.clicked = true
      let answer;
      if (e) {
        clearInterval(this.timer);
        let selected;
        if (e.target.tagName === 'SPAN') {
          selected = e.target.closest('button');
        } else {
          selected = e.target;
        }
        selected.classList.add('selected');
        this.selected = selected.getAttribute('data-id');
        answer = this.quiz.answers.find(a => a.id === this.selected);
      } else {
        answer = { text: '' };
      }
      const { locale } = this.$route.params;
      const correctAnswerResponse = await this.$store.dispatch('quiz/getQuizCorrectAnswer', {
        storyId: this.id,
        answer: answer.id, // send the slugified version of the answer to the backend, prevents special characters from breaking the request
        loc: locale,
        score: this.score
      });
      if (correctAnswerResponse && correctAnswerResponse.is_correct) {
        this.success = true;
        this.modalTitle = this.$i18n.t('quiz.success');
      } else {
        this.score = 0;
      }
      this.correctAnswer = correctAnswerResponse.correct_answer;
      const newTotalScore = this.userTotalScore + this.score;
      this.$store.dispatch('user/updateTempScore', newTotalScore);
      // this.$store.dispatch('users/get', this.$route.params.id);
      this.styleAnswers();
      this.disableBtns();
      this.toggleModal('answerDisplayed');
    },
    disableBtns() {
      const btns = [...document.querySelectorAll('.quiz-button')];
      btns.forEach(btn => {
        btn.classList.add('done');
        btn.setAttribute('disabled', true);
      });
    },
    async styleAnswers() {
      const { answers } = await this.quiz;
      // console.log('desactivate',answers)
      if(!answers) return
      answers.forEach(a => {
        const btn = document.querySelector(`.quiz-button[data-id="${a.id}"]`);
        if (a.id === this.correctAnswer.id) {
          btn.classList.add('correct');
        } else if (a.correct === true) {
          btn.classList.add('correct', 'selected');
        } else {
          btn.classList.add('wrong');
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.quiz {
  .counter {
    &-svg {
      @include size(86px);
      transform: scaleX(-1);
    }
    &-circle {
        fill: none;
        stroke: none;
      }
    &-path {
      stroke-width: 1px;
      &-remaining {
        stroke-width: 2px;
        stroke-linecap: round;
        transform: rotate(-90deg);
        transform-origin: center;
        transition: .1s linear all;
      }
    }
    &-label {
      @include position(absolute, 50% null null 50%);
      transform: translate(-50%, -50%);
      .score {
        display: block;
        @include padding(10px null null);
        @include font('secondary', 'regular');
        font-size: rem(20px);
        line-height: 1;
      }
      .unit {
        @include font('secondary', 'regular');
        text-transform: uppercase;
        line-height: 1;
      }
    }
  }
  &-title {
    @include margin(null null 20px);
    display: flex;
    justify-content: space-between;
    align-items: center;
    &-button {
      @include size(20px);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-end;
      .svg {
        fill: $tertiary;
      }
    }
  }
  &-head {
    display: flex;
    justify-content: space-between;
    &-info {
      display: flex;
      justify-content: space-between;
    }
    &-picture {
      flex-shrink: 0;
      @include aspect-ratio(1,1);
      @include size(86px);
      @include margin(null 14px null null);
      border-radius: 10px;
      overflow: hidden;

      &:after {
        content: '';
        @include position(absolute, 0 0 0 0);
        background-color: rgba($black, .6);
      }

      .year-l {
        @include position(absolute, 50% null null 50%);
        z-index: 1;
        transform: translate(-50%,-43%);
        font-size: rem(28px);
      }

      img {
        @include position(absolute, 0 null null 0);
        @include size(100%);
        object-fit: cover;
      }
    }
    &-content {
      @include margin(null 14px null null);
      align-self: center;
      .h3 {
        line-height: 1.15;
        hyphens: auto;
      }
    }
    &-counter {
      position: relative;
      @include size(86px);
      flex-shrink: 0;
      border-radius: 50%;
      text-align: center;
      &.completed {
        .counter-path,
        .counter-path-remaining {
          stroke: $secondary;
        }
        .counter-label {
          color: $secondary;
        }
      }
      .counter {
        &-path {
          stroke: $grey-dark;
          &-remaining {
            stroke: $tertiary;
          }
        }
      }
    }
  }
  &-content {
    position: relative;
    min-height: 150px;
    @include margin(10px null null);
    .loader {
      @include position(absolute, 50% null null 50%);
      transform: translate(-50%, -50%);
    }
  }
  &-step-1 {
    text-align: center;
    &-title {
      @include margin(45px null 25px);
      font-size: rem(20px);
      line-height: 1.333;
    }
    &-text {
      @include margin(null null 45px);
    }
    .btn {
      width: 100%;
    }
  }
  &-question {
    font-size: rem(20px);
    line-height: 1.65;
  }
  &-answer {
    @include size(100%,49px);
    @include margin(20px null null);
    background-color: $white;
    border: 1px solid $grey-light-alt;
    border-radius: 3px;
    color: $primary;
  }
  &-button {
    @include size(100%);
    @include padding(17px 15px 17px);
    position: relative;
    color: $primary;
    text-align: left;
    span {
      display: inline-block;
      @include position(absolute, 50% 40px null 15px);
      color: $primary;
      transform: translateY(-50%);
    }
    &.selected:disabled {
      color: $primary;
      span {
        color: $primary;
      }
    }
    &:disabled {
      color: rgba($primary, .5);
      cursor: auto;
      span {
        color: rgba($primary, .5);
      }
    }
    &:before {
      content: '';
      display: block;
      @include size(23px);
      @include position(absolute, 13px 15px null null);
      z-index: 2;
      border: 1px solid $grey-light-alt;
      border-radius: 50%;
    }
    &.done {
      &:after {
        content: '';
        display: block;
        @include size(23px);
        @include position(absolute, 13px 15px null null);
        z-index: 1;
        background-color: $grey-light-alt;
        background-position: center;
        background-repeat: no-repeat;
        border: 2px solid $white;
        border-radius: 50%;
      }
      &.selected:after {
        background-color: $secondary;
      }
      &.correct:after {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='8.252' viewBox='0 0 11 8.252'%3E%3Cdefs%3E%3Cstyle%3E.a%7Bfill:%23f6f6f6;%7D%3C/style%3E%3C/defs%3E%3Cg transform='translate(-9.514 -11.635)'%3E%3Cpath class='a' d='M13.64,17.136l-2.751-2.75L9.514,15.761l4.126,4.126,6.874-6.875L19.14,11.635Z'/%3E%3C/g%3E%3C/svg%3E");
        background-size: 11px 8px;
      }
      &.wrong:after {
        background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='utf-8'%3F%3E%3C!-- Generator: Adobe Illustrator 25.2.3, SVG Export Plug-In . SVG Version: 6.00 Build 0) --%3E%3Csvg version='1.1' id='Calque_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 8.3 8.3' style='enable-background:new 0 0 8.3 8.3;' xml:space='preserve'%3E%3Cstyle type='text/css'%3E .st0%7Bfill:%23F6F6F6;%7D%0A%3C/style%3E%3Cpath class='st0' d='M8.2,6.6L8.2,6.6L5.7,4.1l2.5-2.5l0,0c0.1-0.1,0.1-0.3,0-0.4c0,0,0,0,0,0L7,0.1C6.9,0,6.7,0,6.6,0.1l0,0 L4.1,2.6L1.6,0.1l0,0C1.5,0,1.4,0,1.3,0.1c0,0,0,0,0,0L0.1,1.3C0,1.4,0,1.5,0.1,1.6c0,0,0,0,0,0l0,0l2.5,2.5L0.1,6.6l0,0 C0,6.7,0,6.9,0.1,7c0,0,0,0,0,0l1.2,1.2c0.1,0.1,0.3,0.1,0.4,0c0,0,0,0,0,0l0,0l2.5-2.5l2.5,2.5l0,0c0.1,0.1,0.3,0.1,0.4,0 c0,0,0,0,0,0L8.2,7C8.3,6.9,8.3,6.7,8.2,6.6C8.2,6.6,8.2,6.6,8.2,6.6L8.2,6.6z'/%3E%3C/svg%3E%0A");
        background-size: 9px;
      }
    }
  }
  &-modal {
    &-title {
      @include margin(null null 40px);
      color: $primary;
      font-size: rem(18px);
      text-align: center;
      span {
        display: block;
        @include margin(null null 10px);
        color: $secondary;
        font-size: rem(10px);
      }
    }
    &-text {
      @include margin(null null 40px);
      font-size: rem(14px);
    }
     &-counter {
      position: relative;
      @include size(86px);
      @include margin(null auto 40px);
      border-radius: 50%;
      text-align: center;
      .counter {
        &-path,
        &-path-remaining {
          stroke: $secondary;
        }
        &-label {
          .score,
          .unit {
            color: $secondary;
          }
        }
      }
      &.wrong {
        .counter {
          &-path,
          &-path-remaining {
            stroke: $grey-alt;
          }
          &-label {
            .score,
            .unit {
              color: $grey-alt;
            }
          }
        }
      }
    }
  }

  .disabled {
    opacity: .9;
    pointer-events: none
  }
}
.rules {
  .btn {
    @include margin(40px null null);
  }
}
</style>
